<template>
  <base-card :class="{ 'drop-active': canReceiveDrop }">
    <div
      v-if="product_row_width !== -1"
      class="d-flex flex-row-reverse w-100 px-3"
    >
      <base-icon
        icon="kebab-horizontal"
        class="dropdown-toggle kebab-dropdown p-2"
        data-toggle="dropdown"
        target="#rowWidthChangeDropdown"
      />
      <div
        id="rowWidthChangeDropdown"
        class="dropdown-menu dropdown-menu-right"
      >
        <row-width
          :show-cols="1"
          :show-select="true"
          :selected-num="product_row_width"
          @click.native="
            onRowChange(1);
          "
        />
        <row-width
          :show-cols="2"
          :show-select="true"
          :selected-num="product_row_width"
          @click.native="
            onRowChange(2);
          "
        />
        <row-width
          :show-cols="3"
          :show-select="true"
          :selected-num="product_row_width"
          @click.native="
            onRowChange(3);
          "
        />
      </div>
    </div>
    <!-- Product row -->
    <div style="height: 30px;width: 100%;" />

    <div
      class="bg-product p-3"
      :style="`${
        useInstitutionColor
          ? 'background-color: ' + user.profile.institution.color
          : ''
      }`"
      @drop="onDrop"
      @dragover.prevent="onDragOver"
      @dragenter.prevent="onDragEnter"
      @dragleave.prevent="onDragLeave"
    >
      <div v-if="hasProducts" :class="productRowClass">
        <draggable
          v-model="product_images"
          class="product-grid-draggable"
          ghost-class="product-ghost"
          chosen-class="product-chosen"
          :disabled="disabled"
          :delay="isIPad ? 80 : 0"
          :delay-on-touch-only="isIPad"
          :animation="200"
          @end="onProductUpdate"
        >
          <div
            v-for="(product, productIndex) in product_images"
            :key="product.pk"
            :class="productItemClass(productIndex)"
          >
            <product-item
              :pk="product.pk"
              :img="getThumb(product)"
              :title="getTitle(product)"
              :url="getURL(product)"
              :description="product.product.brand.name"
              :editable="true"
              :can-delete="!disabled"
              :style="`${disabled ? '' : 'cursor: move;'}`"
              :product-row-width="content.product_row_width"
              @close="onDeleteItem(product)"
              @edit="onProductEdit($event, product)"
            />
          </div>
        </draggable>
      </div>
      <div
        v-else-if="product_row_width === -1"
        class="flex-column my-3 px-3 d-flex justify-content-center"
      >
        <div class="d-flex row justify-content-center">
          <p>Choose your layout</p>
        </div>
        <div class="d-flex row justify-content-around">
          <row-width :show-cols="1" @click.native="onRowChange(1)" />
          <row-width :show-cols="2" @click.native="onRowChange(2)" />
          <row-width :show-cols="3" @click.native="onRowChange(3)" />
        </div>
      </div>
      <!--
      {display: "flex", "justify-content": "center", "align-items": "center"}
        Display an "Add (more) Product" button at the bottom.

        The @mousedown & @mouseout here is an attempt to get taps/clicks to register
        instead of having the dragging behavior engage (which nullify clicks)
        I (dlam) experimented with different combinations of @click/@mouseover/etc.
        events and felt this @mousedown & @mouseout was most reliable!

        NOTE & reminder:  tablet WILL NOT HAVE a 'onmouseout' event fire,
                          which is why it is duplicated below!

        See:  https://helloseer.monday.com/boards/1326835386/pulses/1327257124
      -->
      <div
        v-if="product_row_width !== -1"
        class="text-center"
        @mousedown="dragDisable"
        @mouseup="draggingEnable"
        @mouseout="draggingEnable"
      >
        <div
          class="d-flex justify-content-center align-items-center add-button p-2"
          :class="{ '--disabled': disabled }"
          @click="openSelector"
        >
          <base-icon
            icon="add-product"
            class="mr-2"
            :class="{ 'd-none': disabled }"
          />
          <base-text>{{ $t(label) }}</base-text>
        </div>
      </div>
    </div>

    <product-selector
      location="product-row"
      disable-computer-upload
      disable-multi-fetch-select
      :use-store="true"
      :show="showSelector"
      :disable-collateral-upload="false"
      :show-shared-collages="false"
      feature="Product"
      @re-open-selector="showSelector == true"
      @cancel="onCancelProductSelectorPopup"
      @complete="onSelectorComplete"
    />
    <product-editor
      :product-image="editableProduct"
      :show-editor="showEditor"
      :is-collateral="isCollateral"
      @delete-product="onEditorDelete"
      @cancel-edit="onEditorCancel"
      @complete="onEditorComplete"
      @closed="showEditor = false"
    />
  </base-card>
</template>

<script>
import draggable from 'vuedraggable';
import whichbrowser from '@/components/mixins/whichbrowser';
import rowWidth from './RowWidth.vue';

export default {
  name: 'ProductWidget',
  components: {
    draggable,
    rowWidth,
  },
  mixins: [whichbrowser],
  props: {
    content: {
      type: Object,
      default: () => ({
        product_images: [],
        product_row_width: -1,
      }),
    },
    dropped: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: 'outreach.product.add',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSelector: false,
      showEditor: false,
      imageSelected: null,
      initialized: false,
      product_images: [],
      useInstitutionColor: false,
      editableProduct: {},
      isCollateral: false,
      canReceiveDrop: false,
      product_row_width: -1,
      canReceiveDropCounter: 0, // https://stackoverflow.com/questions/7110353/html5-dragleave-fired-when-hovering-a-child-element
    };
  },
  computed: {
    user() {
      return this.$store.state.account.user;
    },
    hasProducts() {
      return (
        this.content.product_images && this.content.product_images.length > 0
      );
    },
    productContainerStyle() {
      if (this.content.product_row_width === 2) {
        return {
          width: '352px', height: '469px', 'max-height': '469px', 'max-width': '352px',
        };
      } else if (this.content.product_row_width === 3) {
        return {
          width: '220px', height: '293px', 'max-height': '293px', 'max-width': '220px',
        };
      } else if (this.content.product_row_width === 1) {
        return {
          width: '450px', height: '600px', 'max-height': '600px', 'max-width': '450px',
        };
      }
      return '';
    },
    columnClass() {
      switch (this.product_row_width) {
        case 1:
          return 'col-12'; // Full width (1 column)
        case 2:
          return 'col-6'; // Two columns
        case 3:
          return 'col-4'; // Three columns
        default:
          return 'col-3'; // Default to 4 columns if undefined
      }
    },
    productRowClass() {
      return `product-row product-row--${this.product_row_width}-across`;
    },
  },
  watch: {
    content: {
      handler() {
        // console.log("content", this.initialized);
        this.product_images = this.content.product_images;
        // this.$emit("contentUpdated", this.content.product_images);
      },
      deep: true,
    },
  },
  mounted() {
    // if(this.dropped == true){
    //   this.$emit('open')
    //   this.openSelector()
    // }
    this.product_row_width = this.content.product_row_width;
    this.product_images = this.content.product_images;
  },
  methods: {
    notShowModal() {
      this.showSelector = false;
    },
    rowClass(index) {
      const isThreeWide = true;// this.content.product_row_width !== 1;
      if (isThreeWide) return 'col-4 px-4';
      return `col-${isThreeWide ? 4 : 6} 
        p${index % 2 === 0 ? 'r' : 'l'}-4  
        p${index % 2 === 0 ? 'l' : 'r'}-6 `;
    },
    draggingEnable() {
      // for making the 'Add Product' button respond as expected, instead of the
      // <draggable> behavior, see:
      // https://helloseer.monday.com/boards/1326835386/pulses/1327257124
      this.$store.commit('sections/setDraggable', true);
    },
    dragDisable() {
      this.$store.commit('sections/setDraggable', false);
    },
    formatCollateral(item) {
      // Format collateral structure to make it look like a productImage object
      const url = item.file ?? 'https://';
      return {
        image_url: item.image_url,
        product: {
          brand: {
            name: '',
          },
          external_id: -1,
          images: [
            {
              image_url: item.image_url,
            },
          ],
          price: 0,
          site_url: url,
          title: '',
        },
        site_url: url,
      };
    },
    onCollateralSelected(collateral) {
      this.editableProduct = this.formatCollateral(collateral);
      this.isCollateral = true;
      this.showSelector = false;
      this.showEditor = true;
    },
    onDragOver(evt) {
      // evt.preventDefault();
      // console.log('over', evt);
    },
    onDragEnter(evt) {
      if (this.disabled) return;
      if (evt.dataTransfer.types.includes('product')) {
        this.canReceiveDrop = true;
        this.canReceiveDropCounter += 1;
      }
    },
    onDragLeave(evt) {
      if (this.disabled) return;
      this.canReceiveDropCounter -= 1;

      if (this.canReceiveDropCounter <= 0) {
        this.canReceiveDrop = false;
        this.canReceiveDropCounter = 0;
      }
    },
    onDrop(evt) {
      if (this.disabled) return;
      // Todo: Add product with the selected image.
      console.log('onDrop', evt);
      if (evt.dataTransfer.getData('product')) {
        // Default to 3 in case a product is dropped from the Chrome extension
        if (this.product_row_width === -1) {
          this.onRowChange(3, false);
        }
        const data = JSON.parse(evt.dataTransfer.getData('product'));
        const products = [
          {
            image_url: data.image_url,
            site_url: data.site_url,
            product: data, // MAGIC_PRODUCT_METADATA
          },
        ];
        this.onSaveProduct(products);

        // Resets the content
        this.canReceiveDrop = false;
        this.canReceiveDropCounter = 0;
      }
    },
    onUpdate(item) {
      console.log('item', item);
      // console.log(JSON.stringify(this.content.product_images, null, 4))
      this.content.product_images = this.product_images;
      this.$emit('contentUpdated', this.content);
    },
    onProductUpdate() {
      // Update content with the new product order
      this.content.product_images = this.product_images;
      this.$emit('contentUpdated', this.content);
    },
    onSaveProduct(products) {
      console.log('onSaveProduct', products);
      this.$store.dispatch('productImage/createItems', products).then(() => {
        this.showEditor = false;
        this.onSelectorComplete({
          event: this.$store.state.productImage.items,
        });
      });
    },
    getTitle(item) {
      return item.title || item.product.title || '';
    },
    getURL(item) {
      return item.product.site_url || '';
    },
    getThumb(item) {
      // console.log('getThumb', item);
      return item.display_image || item.image_url; // return item.images[0].image_url
    },
    openSelector(event) {
      if (this.disabled) return;
      this.showSelector = true;
    },
    onSelectorComplete(event) {
      if (event.mode === 'album' && event.event.length === 1) {
        // single collateral without metadata selected
        this.onCollateralSelected(event.event[0]);
        return;
      } // Other cases handled in onAddImage() method in ProductSelector.vue
      this.showSelector = false;
      this.content.product_images.push(...event.event);
      this.$emit('contentUpdated', this.content);
      this.draggingEnable();
    },
    onCancelProductSelectorPopup(event) {
      this.showSelector = false;
      this.draggingEnable();
    },
    onEditorComplete({ editedProduct, isCollateral }) {
      console.log(`Editor complete: ${isCollateral}`, editedProduct);

      if (isCollateral) {
        this.onSaveProduct([editedProduct]);
      } else {
        // Find the index of the existing product in the list
        const index = this.content.product_images.findIndex(
          (p) => p.pk === editedProduct.pk,
        );

        if (index !== -1) {
          // Replace the existing product while maintaining the same array structure
          this.$set(this.content.product_images, index, editedProduct);
        } else {
          // If the product is new, add it
          this.content.product_images.push(editedProduct);
        }

        // Emit an event to update the parent if necessary
        this.$emit('contentUpdated', this.content);

        // Close the editor
        this.showEditor = false;
        this.draggingEnable();
      }
    },
    onEditorCancel() {
      this.editableProduct = {};
      this.showEditor = false;
      this.draggingEnable();
    },
    onEditorDelete({ isCollateral, product }) {
      if (isCollateral) {
        this.onEditorCancel();
      } else {
        this.onDeleteItem(product);
        this.showEditor = false;
        this.draggingEnable();
      }
    },
    onProductEdit(data, product) {
      product.product.title = data.title;
      product.product.brand.name = data.description;
      product.product.site_url = data.site_url;
      this.onEditorComplete({ editedProduct: product, isCollateral: false });
    },
    onDeleteItem(item) {
      const index = this.content.product_images.indexOf(item);
      this.content.product_images.splice(index, 1);
      this.$emit('contentUpdated', this.content);
    },
    productItemClass(index) {
      return `product-item-wrapper product-item-wrapper--${this.product_row_width}-across`;
    },
    onRowChange(num) {
      this.content.product_row_width = num;
      this.product_row_width = num;
      this.$emit('contentUpdated', this.content);
    },
  },
};
</script>
<style lang="scss" scoped>
.drop-active {
  outline: solid 1px $clementine;
}
.add-button {
  transition: opacity 0.3s ease;
  &:not(.--disabled) {
    cursor: pointer;
  }
  &:hover:not(.--disabled) {
    opacity: 0.4;
  }
}
.kebab-dropdown {
  transition: opacity 0.3s ease;
  cursor: pointer;
  &:hover {
    background-color: #f0f0f0;
  }
  border-radius: '5px';
}

// Custom product layout styles using CSS Grid
.product-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1rem 0;
}

.product-grid-draggable {
  display: grid;
  justify-content: center;
  align-items: flex-start;
  gap: 44px; // 44px spacing between items
  width: 100%;

  // Dynamic grid columns based on product_row_width
  &.product-row--1-across .product-grid-draggable,
  .product-row--1-across & {
    grid-template-columns: 1fr;
  }

  &.product-row--2-across .product-grid-draggable,
  .product-row--2-across & {
    grid-template-columns: repeat(2, 1fr);
  }

  &.product-row--3-across .product-grid-draggable,
  .product-row--3-across & {
    grid-template-columns: repeat(3, 1fr);
  }
}

.product-item-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;

  // Fixed widths for each layout to maintain image dimensions
  &--1-across {
    width: 450px; // Single product width
  }

  &--2-across {
    width: 352px; // Two products width
  }

  &--3-across {
    width: 220px; // Three products width
  }
}
</style>
